import type React from "react"

const swallow = (event: React.SyntheticEvent): void => {
  event.preventDefault()
  event.stopPropagation()
}

type ReadOnlyInteractionGuardProps = Pick<
  React.DOMAttributes<HTMLElement>,
  | "onClickCapture"
  | "onPointerDownCapture"
  | "onMouseDownCapture"
  | "onKeyDownCapture"
>

/**
 * Capture-phase handlers that make a container's subtree non-interactive while
 * leaving it scrollable, for read-only surfaces like version-history snapshots.
 *
 * A `disabled` fieldset only neutralizes native form controls; custom widgets
 * (Radix sliders, `role="button"` tag chips, links) stay live and can reach
 * their mutation handlers. `pointer-events: none` would block them all but also
 * kills wheel/scroll. Spread these onto the read-only container instead:
 * interaction-initiating events are swallowed in the capture phase — so
 * descendant handlers never fire and default navigation/focus/drag is
 * cancelled — while wheel, scroll, and touch events pass through untouched.
 *
 * Returns no handlers when `active` is false, so callers can spread it
 * unconditionally on a container that is only sometimes read-only.
 */
export function readOnlyInteractionGuardProps(
  active = true,
): ReadOnlyInteractionGuardProps {
  if (!active) {
    return {}
  }
  return {
    onClickCapture: swallow,
    onPointerDownCapture: swallow,
    onMouseDownCapture: swallow,
    onKeyDownCapture: swallow,
  }
}
